import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { isRecord, omitKeys } from 'lib/object/objectUtils'
import { buildSearchWithTripChatMobileView } from './tripChatMobileViewUrl'
import ScrollLock from 'components/Common/ScrollLock'
import useAutoUpdatingRef from 'hooks/useAutoUpdatingRef'
import { useOptionalAppHistory } from 'lib/routing/appRouting'
import {
  closeOverlayState,
  closeTopOverlayFromBrowserState,
  consumeIgnoredPopState,
  getTopOverlayId,
  initialTripChatMobileOverlayState,
  isTripChatMobileOverlayHistoryState,
  openOverlayState,
  TRIP_CHAT_MOBILE_OVERLAY_HISTORY_STATE,
  type TripChatMobileOverlayId,
  type TripChatMobileOverlayState,
  type TripChatMobileOverlayTransition,
} from './tripChatMobileOverlayState'

export type { TripChatMobileOverlayId } from './tripChatMobileOverlayState'

interface TripChatMobileOverlayContextValue {
  cartCloseRequest: number
  drawerCloseRequest: number
  menuCloseRequest: number
  hotelSearchCloseRequest: number
  hasOpenOverlay: boolean
  topOverlayId: TripChatMobileOverlayId | undefined
  isOverlayOpen: (overlayId: TripChatMobileOverlayId) => boolean
  openOverlay: (overlayId: TripChatMobileOverlayId) => void
  closeOverlay: (
    overlayId: TripChatMobileOverlayId,
    options?: { shouldRequestOverlayClose?: boolean; shouldStepHistory?: boolean },
  ) => void
  closeTopOverlay: () => void
  openChatFromOverlay: (overlayId: TripChatMobileOverlayId) => boolean
}

const TripChatMobileOverlayContext = createContext<TripChatMobileOverlayContextValue | undefined>(undefined)
const NO_PROGRAMMATIC_BACK_STATE = Symbol('NO_PROGRAMMATIC_BACK_STATE')

type AppHistory = ReturnType<typeof useOptionalAppHistory>

function pushOverlayHistoryEntry(appHistory: AppHistory) {
  if (typeof window === 'undefined') return false

  if (appHistory) {
    appHistory.push(
      {
        pathname: appHistory.location.pathname,
        search: appHistory.location.search,
        hash: appHistory.location.hash,
      },
      {
        ...(appHistory.location.state ?? {}),
        ...TRIP_CHAT_MOBILE_OVERLAY_HISTORY_STATE,
      },
    )
    return true
  }

  window.history.pushState(TRIP_CHAT_MOBILE_OVERLAY_HISTORY_STATE, '', window.location.href)
  return true
}

function stepBackOverlayHistory(appHistory: AppHistory) {
  if (typeof window === 'undefined') return

  if (appHistory) {
    appHistory.go(-1)
    return
  }

  window.history.back()
}

export function TripChatMobileOverlayProvider({ children }: { children: ReactNode }) {
  const appHistory = useOptionalAppHistory()
  const [overlayState, setOverlayState] = useState<TripChatMobileOverlayState>(initialTripChatMobileOverlayState)
  const overlayStateRef = useAutoUpdatingRef(overlayState)
  const programmaticBackStateRef = useRef<unknown>(NO_PROGRAMMATIC_BACK_STATE)

  const applyTransition = useCallback((transition: TripChatMobileOverlayTransition) => {
    overlayStateRef.current = transition.state
    setOverlayState(transition.state)

    if (transition.shouldPushHistory) {
      pushOverlayHistoryEntry(appHistory)
    }

    if (transition.shouldStepBackHistory) {
      programmaticBackStateRef.current = appHistory?.location.state
      stepBackOverlayHistory(appHistory)
    }
  }, [appHistory, overlayStateRef])

  const openOverlay = useCallback((overlayId: TripChatMobileOverlayId) => {
    applyTransition(openOverlayState(overlayStateRef.current, overlayId))
  }, [applyTransition, overlayStateRef])

  const closeOverlay = useCallback((
    overlayId: TripChatMobileOverlayId,
    options: { shouldRequestOverlayClose?: boolean; shouldStepHistory?: boolean } = {},
  ) => {
    applyTransition(closeOverlayState(overlayStateRef.current, overlayId, {
      shouldRequestOverlayClose: options.shouldRequestOverlayClose ?? true,
      shouldStepHistory: options.shouldStepHistory ?? true,
    }))
  }, [applyTransition, overlayStateRef])

  const openChatFromOverlay = useCallback((overlayId: TripChatMobileOverlayId) => {
    if (typeof window === 'undefined' || getTopOverlayId(overlayStateRef.current.overlayStack) !== overlayId) return false

    let nextState = overlayStateRef.current
    for (const id of [...nextState.overlayStack].reverse()) {
      nextState = closeOverlayState(nextState, id, {
        shouldRequestOverlayClose: true,
        shouldStepHistory: false,
      }).state
    }
    applyTransition({ state: nextState, shouldPushHistory: false, shouldStepBackHistory: false })

    const location = appHistory?.location ?? window.location
    const currentState: unknown = appHistory ? appHistory.location.state : window.history.state
    let navigationState = currentState
    if (isRecord(currentState)) navigationState = omitKeys(['tripChatMobileOverlay'], currentState)
    const search = buildSearchWithTripChatMobileView(location.search, 'chat')
    if (appHistory) {
      appHistory.replace({ pathname: location.pathname, search, hash: location.hash }, navigationState)
    } else {
      window.history.replaceState(navigationState, '', `${location.pathname}${search}${location.hash}`)
      window.dispatchEvent(new PopStateEvent('popstate', { state: navigationState }))
    }
    return true
  }, [appHistory, applyTransition, overlayStateRef])

  const closeTopOverlay = useCallback(() => {
    const topOverlayId = getTopOverlayId(overlayStateRef.current.overlayStack)
    if (!topOverlayId) return
    closeOverlay(topOverlayId)
  }, [closeOverlay, overlayStateRef])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleBackNavigation = (nextLocationState: unknown) => {
      const currentState = overlayStateRef.current
      if (currentState.ignoreNextPopState) {
        const nextState = consumeIgnoredPopState(currentState)
        overlayStateRef.current = nextState
        setOverlayState(nextState)
        programmaticBackStateRef.current = NO_PROGRAMMATIC_BACK_STATE
        return
      }

      if (
        currentState.overlayHistoryDepth === 0 ||
        isTripChatMobileOverlayHistoryState(nextLocationState) ||
        (
          programmaticBackStateRef.current !== NO_PROGRAMMATIC_BACK_STATE &&
          Object.is(programmaticBackStateRef.current, nextLocationState)
        )
      ) {
        return
      }

      applyTransition(closeTopOverlayFromBrowserState(currentState))
    }

    if (appHistory) {
      return appHistory.listen(({ action, location }) => {
        if (action === 'POP') {
          handleBackNavigation(location.state)
        }
      })
    }

    const handleWindowPopState = (event: PopStateEvent) => {
      handleBackNavigation(event.state)
    }

    window.addEventListener('popstate', handleWindowPopState)
    return () => window.removeEventListener('popstate', handleWindowPopState)
  }, [appHistory, applyTransition, overlayStateRef])

  const value = useMemo<TripChatMobileOverlayContextValue>(() => {
    const topOverlayId = getTopOverlayId(overlayState.overlayStack)

    return {
      cartCloseRequest: overlayState.cartCloseRequest,
      drawerCloseRequest: overlayState.drawerCloseRequest,
      menuCloseRequest: overlayState.menuCloseRequest,
      hotelSearchCloseRequest: overlayState.hotelSearchCloseRequest,
      hasOpenOverlay: overlayState.overlayStack.length > 0,
      topOverlayId,
      isOverlayOpen: overlayId => overlayState.overlayStack.includes(overlayId),
      openOverlay,
      closeOverlay,
      closeTopOverlay,
      openChatFromOverlay,
    }
  }, [closeOverlay, closeTopOverlay, openChatFromOverlay, openOverlay, overlayState])

  return (
    <TripChatMobileOverlayContext.Provider value={value}>
      <ScrollLock isActive={overlayState.overlayStack.length > 0} />
      {children}
    </TripChatMobileOverlayContext.Provider>
  )
}

export function useTripChatMobileOverlay() {
  const context = useContext(TripChatMobileOverlayContext)

  if (!context) {
    throw new Error('useTripChatMobileOverlay must be used within TripChatMobileOverlayProvider')
  }

  return context
}

/** Returns undefined outside the mobile overlay provider (e.g. desktop). */
export function useTripChatMobileOverlayOptional() {
  return useContext(TripChatMobileOverlayContext)
}
